import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Registry of available custom queries
const QUERY_DEFINITIONS = {
  'damaged-stations': {
    title: 'Damaged Stations Data Query',
    endpoint: '/api/queries/damaged-stations',
    columns: [
      { key: 'stationNumber', label: 'Station Number' },
      { key: 'sticker_a81', label: 'Sticker', isImage: true },
      { key: 'stationAndPlinth_b24', label: 'Station & Plinth', isImage: true },
      { key: 'loop11', label: 'Loop 11', isImage: true },
      { key: 'loop12', label: 'Loop 12', isImage: true },
      { key: 'loop13', label: 'Loop 13', isImage: true },
      { key: 'loop14', label: 'Loop 14', isImage: true },
      { key: 'loop15', label: 'Loop 15', isImage: true },
      { key: 'loop16', label: 'Loop 16', isImage: true },
      { key: 'lane2', label: 'Lane 2', isImage: true },
      { key: 'lane2_ed7', label: 'Lane 2 (ed7)', isImage: true },
      { key: 'general', label: 'General', isImage: true },
    ],
  },
};

function QueryView() {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [hasData, setHasData] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [lightboxLabel, setLightboxLabel] = useState('');
  const limit = 50;

  const queryDef = QUERY_DEFINITIONS[queryId];

  const fetchData = useCallback(async () => {
    if (!queryDef) return;
    try {
      setLoading(true);
      const response = await axios.get(queryDef.endpoint, {
        params: { page, limit, search, sortBy, sortOrder, hasData: hasData ? 'true' : '' },
        withCredentials: true,
      });
      setData(response.data.data);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
      setError('');
    } catch (err) {
      console.error('Query error:', err);
      setError('Failed to load query results.');
    } finally {
      setLoading(false);
    }
  }, [queryDef, page, search, sortBy, sortOrder, hasData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keyboard handler for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxUrl) {
        setLightboxUrl(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxUrl]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleHasDataToggle = () => {
    setHasData(prev => !prev);
    setPage(1);
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const getSortIcon = (key) => {
    if (sortBy !== key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortOrder === 'ASC' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (!queryDef) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-600">Query not found.</p>
        <button onClick={() => navigate('/data')} className="mt-4 text-blue-600 hover:underline">
          Back to Data
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/data')}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back to Data"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{queryDef.title}</h1>
            <p className="text-sm text-gray-500">
              {total.toLocaleString()} {total === 1 ? 'record' : 'records'}
            </p>
          </div>
        </div>
      </div>

      {/* Search & controls */}
      <div className="card p-3 mb-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="flex gap-2" style={{ maxWidth: '320px' }}>
            <div className="relative flex-1">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search records..."
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute right-3 top-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button type="submit" className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Search
            </button>
          </form>
          {search && (
            <button
              onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
              className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}

          {/* Has Data checkbox */}
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none whitespace-nowrap">
            <input
              type="checkbox"
              checked={hasData}
              onChange={handleHasDataToggle}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            With data only
          </label>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Pagination */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span>{page} / {totalPages || 1}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-600">Loading...</div>
          ) : data.length === 0 ? (
            <div className="text-center py-12 text-gray-600">No records found</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {queryDef.columns.map((col) => (
                    <th
                      key={col.key}
                      className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort(col.key)}
                    >
                      <div className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {col.label}
                        {getSortIcon(col.key)}
                      </div>
                      <div className="text-[10px] font-normal text-gray-400 mt-0.5 normal-case tracking-normal">
                        {col.key}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {queryDef.columns.map((col) => (
                      <td key={col.key} className="px-3 py-2 text-sm text-gray-900">
                        {col.isImage && row[col.key] ? (
                          <img
                            src={row[col.key]}
                            alt={col.label}
                            className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                            title={`Click to enlarge — ${col.label}`}
                            onClick={() => {
                              setLightboxUrl(row[col.key]);
                              setLightboxLabel(`Station ${row.stationNumber} — ${col.label}`);
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <span className="whitespace-nowrap">
                            {row[col.key] != null && row[col.key] !== '' ? String(row[col.key]) : '-'}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center"
          onClick={() => setLightboxUrl(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            title="Close (Esc)"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Label */}
          <div className="absolute top-4 left-4 text-white">
            <h3 className="text-lg font-semibold">{lightboxLabel}</h3>
          </div>

          {/* Image */}
          <div onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxUrl}
              alt={lightboxLabel}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
                    <rect fill="#374151" width="400" height="300"/>
                    <text fill="#9CA3AF" font-family="sans-serif" font-size="16" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Image not available</text>
                  </svg>
                `);
              }}
            />
          </div>

          {/* Keyboard hint */}
          <div className="absolute bottom-4 right-4 text-gray-400 text-xs">
            Esc to close
          </div>
        </div>
      )}
    </div>
  );
}

export default QueryView;
